import { motion } from "framer-motion";
import { ArrowRight, Compass, Calendar } from "lucide-react";
import { Link } from "react-router-dom";

const HERO_BG = "https://images.unsplash.com/photo-1532205924750-7e84f03402b2?crop=entropy&cs=srgb&fm=jpg&w=1920&q=80";

export function HeroSection() {
  return (
    <section
      data-testid="hero-section"
      className="relative min-h-[92vh] flex items-end overflow-hidden"
    >
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src={HERO_BG}
          alt="Swimmer training underwater"
          className="w-full h-full object-cover"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-near-black via-near-black/70 to-near-black/30" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pb-20 md:pb-28 pt-32 w-full">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          data-testid="hero-eyebrow"
          className="font-inter font-light text-xs tracking-[0.25em] uppercase text-solar-orange mb-6"
        >
          APAC Swimming Portal
        </motion.p>

        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          data-testid="hero-headline"
          className="font-inter font-semibold text-4xl sm:text-5xl md:text-6xl lg:text-7xl tracking-tighter leading-[1.05] text-white max-w-4xl"
        >
          APAC Swimming, Organised.
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          data-testid="hero-subcopy"
          className="font-inter font-normal text-base md:text-lg text-amber-pale/90 max-w-2xl mt-6 leading-relaxed"
        >
          The digital hub for swimmers, clubs, meets, and swim-travel across Asia Pacific. AI-assisted news, discovery, and community.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.55 }}
          className="flex flex-wrap gap-4 mt-10"
        >
          <Link
            to="/sports"
            data-testid="hero-cta-primary"
            className="inline-flex items-center gap-2 bg-solar-orange text-near-black font-inter font-semibold text-sm px-8 py-3.5 rounded-sm hover:bg-amber-dusk hover:text-white transition-colors duration-150 group"
          >
            Read Latest
            <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform duration-150" />
          </Link>
          <Link
            to="/travel"
            data-testid="hero-cta-secondary"
            className="inline-flex items-center gap-2 border-2 border-white/30 text-white font-inter font-medium text-sm px-8 py-3.5 rounded-sm hover:border-solar-orange hover:text-solar-orange transition-colors duration-150 group"
          >
            <Compass size={16} />
            Explore Destinations
          </Link>
          <Link
            to="/meets"
            data-testid="hero-cta-secondary-2"
            className="inline-flex items-center gap-2 border-2 border-white/30 text-white font-inter font-medium text-sm px-8 py-3.5 rounded-sm hover:border-solar-orange hover:text-solar-orange transition-colors duration-150 group"
          >
            <Calendar size={16} />
            Upcoming Meets
          </Link>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="flex flex-wrap gap-8 md:gap-16 mt-16 pt-8 border-t border-white/10"
        >
          {[
            { num: "500+", label: "Articles" },
            { num: "30+", label: "Destinations" },
            { num: "200+", label: "Clubs Listed" },
          ].map((stat) => (
            <div key={stat.label} data-testid={`hero-stat-${stat.label.toLowerCase().replace(/\s/g,'-')}`}>
              <span className="font-inter font-semibold text-2xl md:text-3xl text-solar-orange">
                {stat.num}
              </span>
              <span className="block font-inter font-light text-xs tracking-[0.15em] uppercase text-warm-gray mt-1">
                {stat.label}
              </span>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
